import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Car, Plus, Save, Trash2, Edit3, Eye, EyeOff, ChevronUp, ChevronDown, X, Users, Briefcase } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { extractApiError } from "../../lib/apiError";

export default function VehiclesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/vehicles`);
      setItems(res.data || []);
    } catch (e) { toast.error(extractApiError(e, "Failed to load")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload, id) => {
    try {
      if (id) await axios.put(`${API}/admin/vehicles/${id}`, payload);
      else await axios.post(`${API}/admin/vehicles`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(extractApiError(e, "Failed to save")); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vehicle "${v.title}"?`)) return;
    try { await axios.delete(`${API}/admin/vehicles/${v.vehicle_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(extractApiError(e, "Failed to delete")); }
  };

  const togglePublished = async (v) => {
    try { await axios.put(`${API}/admin/vehicles/${v.vehicle_id}`, { published: !v.published }); load(); }
    catch (e) { toast.error(extractApiError(e, "Failed")); }
  };

  const move = async (index, dir) => {
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const list = [...items];
    const [m] = list.splice(index, 1);
    list.splice(newIdx, 0, m);
    setItems(list); setReordering(true);
    try { await axios.post(`${API}/admin/vehicles/reorder`, { ordered_ids: list.map(i => i.vehicle_id) }); }
    catch (e) { toast.error(extractApiError(e, "Reorder failed")); load(); }
    finally { setReordering(false); }
  };

  return (
    <div data-testid="vehicles-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Car className="w-5 h-5 text-[#C5A059]" /> Fleet
          </h2>
          <p className="text-white/40 text-xs mt-1">
            Vehicles available for assignment. The booking form's passenger / luggage selectors are capped at the largest vehicle's limits. If only one vehicle can serve a request, it's auto-assigned for internal use (silent to the customer).
          </p>
        </div>
        <Button onClick={() => setEditing("new")} className="btn-gold" data-testid="vehicles-add-btn"><Plus className="w-4 h-4 mr-2" /> Add vehicle</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8 text-center text-white/50">No vehicles yet — add one to enable smart auto-assignment.</div>
      ) : (
        <div className="space-y-2">
          {items.map((v, idx) => (
            <div key={v.vehicle_id} className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4 flex items-center gap-3" data-testid={`vehicle-row-${v.vehicle_id}`}>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(idx, -1)} disabled={reordering || idx === 0} className="text-white/30 hover:text-[#C5A059] disabled:opacity-20"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} disabled={reordering || idx === items.length - 1} className="text-white/30 hover:text-[#C5A059] disabled:opacity-20"><ChevronDown className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] text-white/30 font-mono">#{idx + 1}</span>
                  {!v.published && <span className="text-[10px] uppercase text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded-sm">Hidden</span>}
                </div>
                <p className="text-white font-medium truncate">{v.title}</p>
                <div className="flex items-center gap-4 text-white/50 text-xs mt-1">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Max {v.max_passengers} pax</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> Max {v.max_luggage} luggage</span>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => togglePublished(v)} className="text-white/60 hover:text-[#C5A059]" data-testid={`vehicle-toggle-${v.vehicle_id}`}>{v.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</Button>
                <Button variant="ghost" size="sm" onClick={() => setEditing(v)} className="text-white/60 hover:text-[#C5A059]" data-testid={`vehicle-edit-${v.vehicle_id}`}><Edit3 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(v)} className="text-white/60 hover:text-red-400" data-testid={`vehicle-delete-${v.vehicle_id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <VehicleEditor initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSave={(p) => save(p, editing === "new" ? null : editing.vehicle_id)} />
      )}
    </div>
  );
}

function VehicleEditor({ initial, onClose, onSave }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [pax, setPax] = useState(initial?.max_passengers?.toString() ?? "4");
  const [lug, setLug] = useState(initial?.max_luggage?.toString() ?? "4");
  const [published, setPublished] = useState(initial ? !!initial.published : true);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) { toast.error("Title is required"); return; }
    const p = parseInt(pax, 10); const l = parseInt(lug, 10);
    if (Number.isNaN(p) || p < 1) { toast.error("Max passengers must be >= 1"); return; }
    if (Number.isNaN(l) || l < 0) { toast.error("Max luggage must be >= 0"); return; }
    setSaving(true);
    try { await onSave({ title: title.trim(), max_passengers: p, max_luggage: l, published }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="vehicle-editor-modal">
      <form onSubmit={submit} className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">{initial ? "Edit vehicle" : "New vehicle"}</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-white/50 text-[10px] uppercase mb-1 block">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="input-dark" required maxLength={120} placeholder="Mercedes V-Class" data-testid="vehicle-editor-title" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-[10px] uppercase mb-1 block">Max passengers</label>
              <Input type="number" min="1" max="99" value={pax} onChange={(e) => setPax(e.target.value)} className="input-dark font-mono" data-testid="vehicle-editor-pax" />
            </div>
            <div>
              <label className="text-white/50 text-[10px] uppercase mb-1 block">Max luggage</label>
              <Input type="number" min="0" max="99" value={lug} onChange={(e) => setLug(e.target.value)} className="input-dark font-mono" data-testid="vehicle-editor-luggage" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="accent-[#C5A059]" data-testid="vehicle-editor-published" /> Published
          </label>
        </div>
        <div className="p-5 border-t border-white/5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button type="submit" disabled={saving} className="btn-gold" data-testid="vehicle-editor-save"><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save"}</Button>
        </div>
      </form>
    </div>
  );
}
